// The FAQ as written in code, before staff edited it in the admin (faq_groups / faq_items, Admin →
// FAQ). Plain data (no database code), here so the seed can use it; @gaintplay/core/faq re-exports it.
//  - The seed copies it once into the empty tables (seed/faq.ts), so the first deploy changes
//    nothing users see.
//  - The /faq page is built from it, and shows it until the live FAQ arrives or when it can't.
//  - The support assistant answers from it when the live FAQ can't be read.
// The shape is also what GET /api/public/faq answers. Plain text only: an optional `link` is shown
// after the answer on the page but is not part of the structured data. Where a number is an admin
// setting, the answer says "right now".

export interface FaqItem {
  /** Stable anchor, e.g. /faq#pending-coins opens that answer. */
  id: string;
  q: string;
  a: string;
  link?: { to: string; label: string };
}

export interface FaqGroup {
  /** The topic's anchor, e.g. /faq#cashing-out. */
  id: string;
  title: string;
  items: FaqItem[];
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    items: [
      {
        id: 'is-it-free',
        q: 'Is GaintPlay really free?',
        a: 'Yes. You never pay to join or to cash out. Advertisers pay partner networks for new players and customers, the networks pay us for each task you complete, and we share that with you in coins.',
        link: { to: '/gift-cards', label: 'Where the money comes from' },
      },
      {
        id: 'coin-value',
        q: 'How much is a coin worth?',
        a: '1,000 coins = $1.00. Every task shows its reward in coins before you start, so you always know what you will get.',
      },
      {
        id: 'who-can-join',
        q: 'Who can join?',
        a: 'Anyone with an email address who is old enough to use the offers they complete in their country. It is one account per person, and you confirm your email before your first cash-out.',
      },
      {
        id: 'countries',
        q: 'Which countries is GaintPlay available in?',
        a: 'What you can earn depends on where you are. Partners target their offers and surveys by country and device, so the walls show what is open to you. The Cash out page lists the gift cards for your country, and many brands work worldwide.',
      },
      {
        id: 'multiple-accounts',
        q: 'Can I have more than one account?',
        a: 'No. It is one account per person and per household device. We look for accounts that share a device or payout details, and cash-outs from duplicate accounts are held or declined. Accounts that break this rule can be closed.',
        link: { to: '/terms', label: 'Read the Terms of Service' },
      },
      {
        id: 'old-account',
        q: 'I had an account on the old GaintPlay. Can I keep my balance?',
        a: 'Yes, until 30 November 2026. Sign up with any email, then connect your old account from your Wallet; we send a link to your old email address to confirm it is yours. The old account must still be active and have earned or cashed out since 21 August 2026; accounts without that activity were deleted on the old system. Only your confirmed coin balance comes across. Up to 2,000 coins arrive straight away, larger balances are approved by our team first, and each old account can be connected once.',
      },
    ],
  },
  {
    id: 'earning',
    title: 'Earning coins',
    items: [
      {
        id: 'how-to-earn',
        q: 'How do I earn coins?',
        a: 'Open Earn, pick an offer wall or survey router, and complete a task: answer a survey, reach a level in a game, try an app or start a free trial. When the partner confirms it, the coins land in your balance.',
        link: { to: '/ways-to-earn', label: 'See every way to earn' },
      },
      {
        id: 'different-offers',
        q: 'Why do my friends see different offers and rewards?',
        a: 'Partners set the rewards and choose who sees each offer by country, device and profile. Two people can see different tasks, or different rewards for the same task.',
      },
      {
        id: 'vpn',
        q: 'Can I use a VPN or proxy?',
        a: 'No. Turn it off before you open an offer wall. Partners check where you really are, and tasks done through a VPN, proxy or emulator are usually rejected or reversed. Accounts that use them can be closed and their coins removed.',
      },
      {
        id: 'daily-bonus',
        q: 'How does the daily bonus work?',
        a: 'Claim it once a day on Rewards to move along a 7-day streak. Right now it pays 10 coins on day 1, rising to 60 coins on day 7. Miss a day and the streak starts again at day 1. It unlocks once your email is confirmed and you have completed an offer in the last 7 days.',
      },
      {
        id: 'referrals',
        q: 'How do referrals work?',
        a: 'Share your invite link from Rewards. You get 5% of every coin your friends earn from offers, for as long as they use GaintPlay. We pay it, so nothing is taken from your friends. If their reward is pending or reversed, so is your share.',
      },
    ],
  },
  {
    id: 'pending-missing',
    title: 'Pending, missing and reversed coins',
    items: [
      {
        id: 'pending-coins',
        q: 'Why are some of my coins pending?',
        a: 'Bigger rewards wait so the partner can confirm the task was done properly. Right now, rewards of 2,000 coins or more wait 7 days, from 4,000 coins 15 days, and from 15,000 coins 30 days. Some partners use their own schedule. Smaller rewards are available straight away.',
        link: { to: '/how-it-works', label: 'How holds work' },
      },
      {
        id: 'pending-release',
        q: 'When will my pending coins be available?',
        a: 'Your Wallet shows the release date for every pending reward. On that date the coins move to your available balance on their own; you do not need to do anything.',
      },
      {
        id: 'missing-coins',
        q: 'A task didn’t pay. What now?',
        a: 'Partners credit coins after they confirm, which usually takes minutes but can take longer. If it has been more than a day, contact the offer wall’s support from inside the wall, with a screenshot of what you did. If that does not solve it, ask us in Help.',
      },
      {
        id: 'reversals',
        q: 'Why were coins taken back from my balance?',
        a: 'The partner reversed the task. That happens when the advertiser decides it did not qualify, for example after a refund, a repeat install, a VPN or a failed check. Reversals show in your Wallet history.',
      },
      {
        id: 'negative-balance',
        q: 'Can my balance go below zero?',
        a: 'Yes, if a partner reverses coins you have already spent. Cash-outs pause until new earnings bring your balance back above zero.',
      },
    ],
  },
  {
    id: 'cashing-out',
    title: 'Cashing out',
    items: [
      {
        id: 'minimum',
        q: 'What is the minimum cash-out?',
        a: 'Gift cards start at $1 (1,000 coins). Right now your first cash-out unlocks once you have 2,500 coins ($2.50) available. After that there is no minimum: cash out any card you can afford.',
      },
      {
        id: 'cashout-time',
        q: 'How long does a cash-out take?',
        a: 'Every cash-out is reviewed first. Most are approved in minutes, and we email you as soon as your card is ready. Bigger amounts can take longer because a person checks them too.',
      },
      {
        id: 'delivery',
        q: 'How is my gift card delivered?',
        a: 'The code appears in your account under Your cash-outs, and we email you when it is ready. Top-ups and account payouts go straight to the account you entered.',
        link: { to: '/gift-cards', label: 'How cash-outs work' },
      },
      {
        id: 'why-review',
        q: 'Why is every cash-out reviewed?',
        a: 'To stop fraud. When fake or stolen accounts cash out, advertisers claw the money back from the networks and from us. Automated checks, including AI, review each request, and you can ask for a person to look at any decision.',
      },
      {
        id: 'declined',
        q: 'What happens if my cash-out is declined?',
        a: 'The coins go back to your balance, unless the earnings were fraudulent. If an order fails on the gift card provider’s side, the coins come back too.',
      },
      {
        id: 'expiry',
        q: 'Do coins expire?',
        a: 'No. Your coins never expire, so cash out today or save up for a bigger card.',
      },
      {
        id: 'brands',
        q: 'Which gift cards can I get?',
        a: 'Hundreds of brands across shopping, games, food and streaming. The list depends on your country, and every card shows its price in coins before you confirm.',
      },
    ],
  },
  {
    id: 'account',
    title: 'Your account and data',
    items: [
      {
        id: 'delete-account',
        q: 'How do I delete my account?',
        a: 'Email support@gaintplay.com from the address on your account and ask us to delete it. We can send you a copy of your data first. Transaction records are kept for as long as the law requires.',
      },
      {
        id: 'data',
        q: 'What do you do with my data?',
        a: 'We use your email, the tasks you complete and technical data such as your IP address and device to run your account, pay rewards and prevent fraud. Partners get an anonymous ID, never your email.',
        link: { to: '/privacy', label: 'Read the Privacy Policy' },
      },
      {
        id: 'support',
        q: 'How do I contact support?',
        a: 'Once you are signed in, open Help to start a conversation; anything about money or your account goes to a person. You can also email support@gaintplay.com.',
      },
    ],
  },
];
